<template>
  <section class="py-12">
    <div class="container-wrap">
      <h2 class="mb-6 text-2xl font-bold desktop:text-3xl">
        {{ contextData.sectionTitle }}
      </h2>
      <ul
        class="scrollbar-h-px extend-width mb-6 flex w-full overflow-y-hidden overflow-x-scroll desktop:flex-wrap desktop:overflow-x-hidden"
      >
        <li
          v-for="(item, index) in contextStories"
          :key="item.storyTitle"
          class="mr-2 desktop:mb-2"
        >
          <button
            class="w-full rounded-3xl border border-solid border-gray-400 px-4 py-1.5 hover:bg-blue-400 hover:text-white"
            :class="
              current === index
                ? 'bg-blue-400 text-white'
                : 'bg-white text-blue-400'
            "
            :title="item.storyBrand"
            @click="current = index"
          >
            <span class="truncate text-left text-sm font-bold">
              {{ item.storyBrand }}
            </span>
          </button>
        </li>
      </ul>
      <ul>
        <li
          v-for="(item, index) in contextStories"
          v-show="current === index"
          :key="item.storyTitle"
        >
          <div
            :title="item.storyTitle"
            class="element_card block w-full cursor-pointer overflow-hidden rounded shadow-normal shadow-gray-400 hover:shadow-blue-400/50 tablet:flex"
            @click="handleClick(item)"
          >
            <div
              class="p-3 tablet:flex tablet:h-80 tablet:w-1/2 tablet:flex-col tablet:justify-between tablet:p-5"
            >
              <div>
                <h3
                  class="mb-2 text-base font-bold tablet:line-clamp-3 tablet:max-h-[5.25rem] tablet:text-lg desktop:line-clamp-2 desktop:max-h-14"
                >
                  {{ item.storyTitle }}
                </h3>
                <p
                  class="mb-2 text-sm text-gray-600 tablet:line-clamp-5 tablet:max-h-[6.25rem] tablet:text-base desktop:line-clamp-4 desktop:max-h-24"
                >
                  {{ item.storyDescription }}
                </p>
              </div>
              <div>
                <div class="flex items-center">
                  <div
                    v-if="item.storyLogo"
                    class="mr-5 w-[78px] basis-[78px] desktop:w-[160px] desktop:basis-[160px]"
                  >
                    <figure
                      class="relative aspect-video w-full overflow-hidden rounded"
                    >
                      <img
                        :src="provideImgUrl(item.storyLogo)"
                        :alt="item.storyBrand"
                        loading="lazy"
                        class="pointer-events-none absolute left-0 top-0 h-full w-full object-contain"
                      />
                    </figure>
                  </div>
                  <div class="flex-1 text-sm desktop:text-base">
                    <p
                      v-if="item.storyCompanyName"
                      class="font-bold tablet:mb-1"
                    >
                      {{ item.storyCompanyName }}
                    </p>
                    <p v-if="item.storyRegion">
                      <strong
                        class="mr-3 after:pl-3 after:font-normal after:content-['|']"
                      >
                        {{ t('region') }}
                      </strong>
                      {{ item.storyRegion }}
                    </p>
                  </div>
                </div>
                <div class="hidden tablet:flex tablet:justify-end">
                  <TheNuxtLink
                    :to="item.storyCtaLink"
                    :title="item.storyTitle"
                    class="element_arrow flex h-8 w-8 basis-8 items-center justify-center rounded text-blue-400"
                  >
                    <IconArrow class="h-5 w-5" direction="right" />
                  </TheNuxtLink>
                </div>
              </div>
            </div>
            <div
              class="relative flex min-h-[200px] w-full items-center overflow-hidden p-3 tablet:h-80 tablet:min-h-0 tablet:w-1/2 tablet:p-5"
            >
              <figure
                class="absolute left-0 top-0 h-full w-full overflow-hidden after:absolute after:inset-0 after:bg-gradient-to-b after:from-black/80 after:to-black/50 after:content-['']"
              >
                <img
                  :src="provideImgUrl(item.storyBg)"
                  :alt="item.storyTitle"
                  loading="lazy"
                  class="pointer-events-none absolute left-0 top-0 h-full w-full object-cover"
                />
              </figure>
              <div class="relative">
                <template v-if="item.testimonial">
                  <IconComment class="mb-3 text-white" />
                  <p
                    class="mb-3 text-base text-white tablet:line-clamp-6 tablet:max-h-[10.5rem] tablet:text-lg"
                  >
                    {{ item.testimonial }}
                  </p>
                </template>
                <div class="flex tablet:items-center">
                  <figure
                    v-if="item.userAvatar"
                    class="h-11 w-11 basis-11 overflow-hidden rounded-full desktop:h-[60px] desktop:w-[60px] desktop:basis-[60px]"
                  >
                    <img
                      :src="provideImgUrl(item.userAvatar)"
                      :alt="item.userName"
                      loading="lazy"
                      class="pointer-events-none h-full w-full object-cover"
                    />
                  </figure>
                  <div class="flex-1 pl-2 text-sm text-white desktop:pl-5">
                    <p v-if="item.userName" class="mb-1">
                      {{ item.userName }}
                    </p>
                    <p v-if="item.userTitle">
                      {{ item.userTitle }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
  import EN_AMERICAS_JSON from '@/assets/data/home/en-americas/section-4.json';
  import EN_ASIA_PACIFIC_JSON from '@/assets/data/home/en-asia-pacific/section-4.json';
  import EN_EMEA_JSON from '@/assets/data/home/en-emea/section-4.json';
  import EN_INT_JSON from '@/assets/data/home/en-int/section-4.json';
  import EN_JP_JSON from '@/assets/data/home/en-jp/section-4.json';
  import EN_KR_JSON from '@/assets/data/home/en-kr/section-4.json';
  import EN_TW_JSON from '@/assets/data/home/en-tw/section-4.json';
  import JA_JP_JSON from '@/assets/data/home/ja-jp/section-4.json';
  import ZH_CN_JSON from '@/assets/data/home/zh-cn/section-4.json';
  import ZH_TW_JSON from '@/assets/data/home/zh-tw/section-4.json';
  import type { ISuccess, ISuccessStory } from '@/assets/types/Home';

  const { t, locale } = useI18n({ useScope: 'local' });
  const { provideImgUrl } = useHomeData();
  const { isCN } = useLanguage();
  const { provideFormatUrl, processLink } = useFormatUrl();

  const current = ref<number>(0);

  const contextData = computed((): ISuccess => {
    switch (locale.value) {
      case EnumLocale.EN_INT:
        return EN_INT_JSON;
      case EnumLocale.EN_AMERICAS:
        return EN_AMERICAS_JSON;
      case EnumLocale.EN_JP:
        return EN_JP_JSON;
      case EnumLocale.EN_TW:
        return EN_TW_JSON;
      case EnumLocale.EN_KR:
        return EN_KR_JSON;
      case EnumLocale.EN_ASIA_PACIFIC:
        return EN_ASIA_PACIFIC_JSON;
      case EnumLocale.EN_EMEA:
        return EN_EMEA_JSON;
      case EnumLocale.ZH_TW:
        return ZH_TW_JSON;
      case EnumLocale.ZH_CN:
        return ZH_CN_JSON;
      case EnumLocale.JA_JP:
        return JA_JP_JSON;
      default:
        return EN_INT_JSON;
    }
  });

  const contextStories = computed(
    (): ISuccessStory[] => contextData.value.successStories
  );

  const handleClick = (storyItem: ISuccessStory): void => {
    if (isCN.value) return;
    processLink(provideFormatUrl(storyItem.storyCtaLink));
  };
</script>

<style lang="scss" scoped>
  .element_card:hover {
    .element_arrow {
      @apply bg-blue-400 text-white;
    }
  }
</style>

<i18n lang="json">
{
  "en-int": {
    "region": "Region"
  },
  "en-jp": {
    "region": "Region"
  },
  "en-tw": {
    "region": "Region"
  },
  "en-kr": {
    "region": "Region"
  },
  "en-americas": {
    "region": "Region"
  },
  "en-asia-pacific": {
    "region": "Region"
  },
  "en-emea": {
    "region": "Region"
  },
  "ja-jp": {
    "region": "地域"
  },
  "zh-cn": {
    "region": "区域"
  },
  "zh-tw": {
    "region": "區域"
  }
}
</i18n>
